import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Refresh, Replay } from "@mui/icons-material";

import { APP_API_V1_BASE } from "../../../config/api";

const DISPATCH_STATUS_OPTIONS = [
  "",
  "pending",
  "processing",
  "dispatched",
  "dead_letter",
  "canceled",
];

const toTitle = (value) =>
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const dispatchStatusColor = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "pending") return "warning";
  if (key === "processing") return "info";
  if (key === "dispatched") return "success";
  if (key === "dead_letter") return "error";
  if (key === "canceled") return "default";
  return "default";
};

const buildParams = (params) => {
  const output = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    output[key] = value;
  });
  return output;
};

function QueueMetric({ label, value }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.2,
        borderRadius: 2,
        border: "1px solid #E2E8F0",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0F172A" }}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function DispatchOperationsPanel({
  deploymentId,
  headers,
  canManageDispatch,
}) {
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState("");

  const [dispatchRows, setDispatchRows] = useState([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchError, setDispatchError] = useState("");
  const [dispatchStatus, setDispatchStatus] = useState("");
  const [dispatchSearch, setDispatchSearch] = useState("");
  const [dispatchPagination, setDispatchPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
    count: 0,
  });

  const [deadLetters, setDeadLetters] = useState([]);
  const [deadLetterLoading, setDeadLetterLoading] = useState(false);
  const [deadLetterError, setDeadLetterError] = useState("");
  const [deadLetterScope, setDeadLetterScope] = useState("DEPLOYMENT");
  const [deadLetterSearch, setDeadLetterSearch] = useState("");
  const [deadLetterPagination, setDeadLetterPagination] = useState({
    limit: 10,
    offset: 0,
    total: 0,
    count: 0,
  });

  const [notice, setNotice] = useState(null);
  const [replayingAll, setReplayingAll] = useState(false);
  const [replayingRowId, setReplayingRowId] = useState("");

  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    setMetricsError("");
    try {
      const res = await axios.get(`${APP_API_V1_BASE}/firmware/dispatch/metrics`, {
        headers,
      });
      setMetrics(res.data || null);
    } catch (err) {
      setMetricsError(
        err?.response?.data?.error || "Failed to load dispatch metrics.",
      );
    } finally {
      setMetricsLoading(false);
    }
  }, [headers]);

  const fetchDeploymentDispatchRows = useCallback(async () => {
    if (!deploymentId) return;
    setDispatchLoading(true);
    setDispatchError("");
    try {
      const res = await axios.get(
        `${APP_API_V1_BASE}/firmware/deployments/${deploymentId}/dispatch`,
        {
          headers,
          params: buildParams({
            status: dispatchStatus,
            search: dispatchSearch,
            limit: dispatchPagination.limit,
            offset: dispatchPagination.offset,
          }),
        },
      );
      setDispatchRows(res.data?.dispatch_rows || []);
      setDispatchPagination((prev) => ({
        ...prev,
        ...(res.data?.pagination || {}),
      }));
    } catch (err) {
      setDispatchError(
        err?.response?.data?.error ||
          "Failed to load deployment dispatch rows.",
      );
      setDispatchRows([]);
    } finally {
      setDispatchLoading(false);
    }
  }, [
    deploymentId,
    headers,
    dispatchStatus,
    dispatchSearch,
    dispatchPagination.limit,
    dispatchPagination.offset,
  ]);

  const fetchDeadLetters = useCallback(async () => {
    setDeadLetterLoading(true);
    setDeadLetterError("");
    try {
      const params = buildParams({
        limit: deadLetterPagination.limit,
        offset: deadLetterPagination.offset,
        search: deadLetterSearch,
        deployment_id:
          deadLetterScope === "DEPLOYMENT" ? deploymentId : undefined,
      });
      const res = await axios.get(
        `${APP_API_V1_BASE}/firmware/dispatch/dead-letters`,
        {
          headers,
          params,
        },
      );
      setDeadLetters(res.data?.dead_letters || []);
      setDeadLetterPagination((prev) => ({
        ...prev,
        ...(res.data?.pagination || {}),
      }));
    } catch (err) {
      setDeadLetterError(
        err?.response?.data?.error || "Failed to load dead-letter rows.",
      );
      setDeadLetters([]);
    } finally {
      setDeadLetterLoading(false);
    }
  }, [
    headers,
    deploymentId,
    deadLetterScope,
    deadLetterSearch,
    deadLetterPagination.limit,
    deadLetterPagination.offset,
  ]);

  const refreshAll = useCallback(async () => {
    setNotice(null);
    await Promise.all([
      fetchMetrics(),
      fetchDeploymentDispatchRows(),
      fetchDeadLetters(),
    ]);
  }, [fetchMetrics, fetchDeploymentDispatchRows, fetchDeadLetters]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    const timer = setInterval(() => {
      fetchMetrics();
    }, 10000);
    return () => clearInterval(timer);
  }, [fetchMetrics]);

  const handleReplayAll = async () => {
    if (!deploymentId) return;
    setNotice(null);
    setReplayingAll(true);
    try {
      const res = await axios.post(
        `${APP_API_V1_BASE}/firmware/deployments/${deploymentId}/dispatch/replay`,
        {},
        { headers },
      );
      setNotice({
        type: "success",
        message:
          res.data?.message ||
          "Dead-letter dispatch rows were scheduled for replay.",
      });
      await refreshAll();
    } catch (err) {
      setNotice({
        type: "error",
        message:
          err?.response?.data?.error ||
          "Failed to replay dead-letter dispatch rows.",
      });
    } finally {
      setReplayingAll(false);
    }
  };

  const handleReplayRow = async (row) => {
    if (!row?.id || !row?.deployment_id) return;
    setNotice(null);
    setReplayingRowId(row.id);
    try {
      const res = await axios.post(
        `${APP_API_V1_BASE}/firmware/deployments/${row.deployment_id}/dispatch/${row.id}/replay`,
        {},
        { headers },
      );
      setNotice({
        type: "success",
        message:
          res.data?.message || `Replay scheduled for dispatch row ${row.id}.`,
      });
      await refreshAll();
    } catch (err) {
      setNotice({
        type: "error",
        message:
          err?.response?.data?.error ||
          "Failed to replay selected dead-letter dispatch row.",
      });
    } finally {
      setReplayingRowId("");
    }
  };

  const queueMetrics = useMemo(() => {
    const queue = metrics?.queue || {};
    const backlog = metrics?.backlog || {};
    return [
      { key: "pending", label: "Pending", value: queue.pending || 0 },
      { key: "processing", label: "Processing", value: queue.processing || 0 },
      {
        key: "due_pending",
        label: "Due Pending",
        value: queue.due_pending || 0,
      },
      {
        key: "dead_letter",
        label: "Dead Letter",
        value: queue.dead_letter || 0,
      },
      {
        key: "overdue_pending",
        label: "Overdue Pending",
        value: backlog.overdue_pending || 0,
      },
      {
        key: "processing_without_publish",
        label: "Processing No Publish",
        value: backlog.processing_without_publish || 0,
      },
    ];
  }, [metrics]);

  const moveDispatchPage = (direction) => {
    setDispatchPagination((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset + direction * prev.limit),
    }));
  };

  const moveDeadLetterPage = (direction) => {
    setDeadLetterPagination((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset + direction * prev.limit),
    }));
  };

  const dispatchHasPrev = dispatchPagination.offset > 0;
  const dispatchHasNext =
    dispatchPagination.offset + dispatchPagination.count < dispatchPagination.total;
  const dispatchStart =
    dispatchPagination.total > 0 ? dispatchPagination.offset + 1 : 0;
  const dispatchEnd =
    dispatchPagination.total > 0
      ? dispatchPagination.offset + dispatchPagination.count
      : 0;

  const deadLetterHasPrev = deadLetterPagination.offset > 0;
  const deadLetterHasNext =
    deadLetterPagination.offset + deadLetterPagination.count <
    deadLetterPagination.total;
  const deadLetterStart =
    deadLetterPagination.total > 0 ? deadLetterPagination.offset + 1 : 0;
  const deadLetterEnd =
    deadLetterPagination.total > 0
      ? deadLetterPagination.offset + deadLetterPagination.count
      : 0;

  return (
    <Paper
      sx={{
        mt: 2.5,
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        border: "1px solid rgba(148,163,184,0.14)",
        boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 1.2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, md: 22 } }}>
            Dispatch Operations
          </Typography>
          <Typography sx={{ color: "#64748B", fontSize: 12 }}>
            Queue metrics, dead-letter replay, and dispatch diagnostics.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={metricsLoading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={refreshAll}
          disabled={metricsLoading || dispatchLoading || deadLetterLoading}
          sx={{ textTransform: "none", borderRadius: 2.5 }}
        >
          Refresh dispatch
        </Button>
      </Box>

      {notice && (
        <Alert severity={notice.type} sx={{ mt: 2, borderRadius: 2.5 }}>
          {notice.message}
        </Alert>
      )}

      {metricsError && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2.5 }}>
          {metricsError}
        </Alert>
      )}

      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            xl: "repeat(6, minmax(0, 1fr))",
          },
          gap: 1.2,
        }}
      >
        {queueMetrics.map((item) => (
          <QueueMetric key={item.key} label={item.label} value={item.value} />
        ))}
      </Box>

      <Divider sx={{ my: 2.2 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 1.2,
          mb: 1.2,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
          This Deployment Dispatch Rows
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            label="Status"
            value={dispatchStatus}
            onChange={(event) => {
              setDispatchStatus(event.target.value);
              setDispatchPagination((prev) => ({ ...prev, offset: 0 }));
            }}
            sx={{ minWidth: 150 }}
          >
            {DISPATCH_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status || "all"} value={status}>
                {status ? toTitle(status) : "All"}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Search"
            value={dispatchSearch}
            onChange={(event) => {
              setDispatchSearch(event.target.value);
              setDispatchPagination((prev) => ({ ...prev, offset: 0 }));
            }}
            sx={{ minWidth: 220 }}
          />
          {canManageDispatch && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<Replay />}
              onClick={handleReplayAll}
              disabled={replayingAll}
              sx={{ textTransform: "none", borderRadius: 2.5 }}
            >
              {replayingAll ? "Replaying..." : "Replay Dead Letters"}
            </Button>
          )}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 11, color: "#64748B", mb: 0.8 }}>
        Dispatch attempts include re-publish cycles when device ACK is not received.
      </Typography>

      {dispatchError && (
        <Alert severity="error" sx={{ mb: 1.2, borderRadius: 2.5 }}>
          {dispatchError}
        </Alert>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
              <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Dispatch Attempts</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Next Retry</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Error</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dispatchLoading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ py: 1.8, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : dispatchRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography sx={{ color: "#64748B" }}>
                    No dispatch rows found for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dispatchRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                      {row.device_name || "Unnamed device"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#64748B",
                        fontFamily: "monospace",
                      }}
                    >
                      {row.device_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={toTitle(row.status)}
                      color={dispatchStatusColor(row.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {row.attempt_count || 0} / {row.max_attempts || 0}
                  </TableCell>
                  <TableCell>{formatDateTime(row.next_retry_at)}</TableCell>
                  <TableCell sx={{ maxWidth: 340 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: row.last_error ? "#991B1B" : "#64748B",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.last_error || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Replay fontSize="small" />}
                      onClick={() => handleReplayRow(row)}
                      disabled={
                        !canManageDispatch ||
                        String(row.status || "").toLowerCase() !== "dead_letter" ||
                        replayingRowId === row.id
                      }
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      {replayingRowId === row.id ? "Replaying..." : "Replay Row"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          mt: 1.2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#64748B" }}>
          Showing {dispatchStart}-{dispatchEnd} of {dispatchPagination.total}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!dispatchHasPrev}
            onClick={() => moveDispatchPage(-1)}
            sx={{ textTransform: "none" }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!dispatchHasNext}
            onClick={() => moveDispatchPage(1)}
            sx={{ textTransform: "none" }}
          >
            Next
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2.2 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 1.2,
          mb: 1.2,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
          Dead Letters
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            label="Scope"
            value={deadLetterScope}
            onChange={(event) => {
              setDeadLetterScope(event.target.value);
              setDeadLetterPagination((prev) => ({ ...prev, offset: 0 }));
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="DEPLOYMENT">This Deployment</MenuItem>
            <MenuItem value="ALL">All Deployments</MenuItem>
          </TextField>
          <TextField
            size="small"
            label="Search"
            value={deadLetterSearch}
            onChange={(event) => {
              setDeadLetterSearch(event.target.value);
              setDeadLetterPagination((prev) => ({ ...prev, offset: 0 }));
            }}
            sx={{ minWidth: 220 }}
          />
        </Box>
      </Box>

      {deadLetterError && (
        <Alert severity="error" sx={{ mb: 1.2, borderRadius: 2.5 }}>
          {deadLetterError}
        </Alert>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
              <TableCell sx={{ fontWeight: 700 }}>Deployment</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Dead Lettered At</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deadLetterLoading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ py: 1.8, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : deadLetters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography sx={{ color: "#64748B" }}>
                    No dead-letter rows found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              deadLetters.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography
                      sx={{ fontFamily: "monospace", fontSize: 11, color: "#0F172A" }}
                    >
                      {row.deployment_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                      {row.device_name || "Unnamed device"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        color: "#64748B",
                      }}
                    >
                      {row.device_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDateTime(row.dead_lettered_at)}</TableCell>
                  <TableCell sx={{ maxWidth: 340 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#991B1B",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.last_error || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Replay fontSize="small" />}
                      onClick={() => handleReplayRow(row)}
                      disabled={!canManageDispatch || replayingRowId === row.id}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      {replayingRowId === row.id ? "Replaying..." : "Replay Row"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          mt: 1.2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#64748B" }}>
          Showing {deadLetterStart}-{deadLetterEnd} of {deadLetterPagination.total}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!deadLetterHasPrev}
            onClick={() => moveDeadLetterPage(-1)}
            sx={{ textTransform: "none" }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!deadLetterHasNext}
            onClick={() => moveDeadLetterPage(1)}
            sx={{ textTransform: "none" }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
